import React, { useState, useMemo } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { ValuationAsset, Entity, EntityGroup } from '../types/valuation';
import GroupingSelector, { type GroupingConfig } from './GroupingSelector';
import FilterSelector, { type FilterConfig } from './FilterSelector';
import { groupAssets, type GroupedData } from '../utils/grouping';
import { generateAssetTypes } from '../data/assetTypes';
import { generateAssetSectors } from '../data/assetSubSectors';
import { generateClassificationTrees, type ClassificationTreeItem } from '../data/classificationTrees';

interface ValuationTableProps {
  assets: ValuationAsset[];
  entities: Entity[];
  entityGroup: EntityGroup;
  totalValue: number;
}

const formatCurrency = (value: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency === 'GBP' ? 'GBP' : currency === 'EUR' ? 'EUR' : 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

// Helper function to get all asset IDs for a classification tree item (including from all descendant levels)
const getAssetIdsForTreeItem = (treeItems: ClassificationTreeItem[], itemId: number): (string | number)[] => {
  const assetIds = new Set<string | number>();
  const visitedItems = new Set<number>(); // Prevent infinite loops
  
  const collectAssetIds = (id: number, depth = 0) => {
    // Prevent infinite recursion
    if (visitedItems.has(id)) return;
    visitedItems.add(id);
    
    const item = treeItems.find(item => item.id === id);
    if (!item) return;
    
    // If this item has direct asset IDs, add them to our collection
    if (item.assetIds && Array.isArray(item.assetIds) && item.assetIds.length > 0) {
      item.assetIds.forEach((assetId: string | number) => {
        if (assetId !== null && assetId !== undefined) {
          assetIds.add(assetId);
        }
      });
    }
    
    // Find all direct children of this item and recursively traverse them
    const children = treeItems.filter(child => child.parentId === id);
    
    for (const child of children) {
      collectAssetIds(child.id, depth + 1); // Recursive call - will go as deep as needed
    }
  };
  
  // Start the recursive collection from the requested item
  collectAssetIds(itemId);
  const result = Array.from(assetIds);
  return result;
};

const ValuationTableV1: React.FC<ValuationTableProps> = ({ 
  assets, 
  entities, 
  entityGroup,
  totalValue 
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());
  const [selectedGroupings, setSelectedGroupings] = useState<GroupingConfig[]>([
    { id: 'entity', label: 'Entity', type: 'entity' }
  ]);
  const [filters, setFilters] = useState<FilterConfig>({
    entities: [],
    assetClasses: [],
    sectors: [],
    portfolios: [],
    classificationTree: null,
    asAtDate: new Date(),
    reportable: 'reportable'
  });
  const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | number | null>(null);
  const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [assetsData, setAssetsData] = useState<ValuationAsset[]>(assets);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('last-30-days');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // Create lookup maps for asset types and sub sectors
  const assetTypes = useMemo(() => generateAssetTypes(), []);
  const assetSubSectors = useMemo(() => generateAssetSectors(), []);
  
  const assetTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    assetTypes.forEach(type => {
      map.set(type.id, type.name);
    });
    return map;
  }, [assetTypes]);

  const assetSubSectorMap = useMemo(() => {
    const map = new Map<number, string>();
    assetSubSectors.forEach(subSector => {
      map.set(subSector.id, subSector.name);
    });
    return map;
  }, [assetSubSectors]);

  // Create sector mapping for filtering
  const sectorMap = useMemo(() => {
    const map = new Map<number, string>();
    
    // Create a basic sector mapping based on sectorId patterns in the data
    // We'll derive sector names from the subsector groupings
    const sectorNames = new Map<number, string>([
      [1, 'Alternative Investments'],
      [2, 'Equities'], 
      [3, 'Fixed Income'],
      [4, 'Cash & Cash Equivalents'],
      [5, 'Real Estate'],
      [6, 'Diversified Funds'],
      [7, 'Digital Assets'],
      [8, 'Commodities'],
      [9, 'Other'],
      [10, 'Tangible Assets'],
      [11, 'Private Markets']
    ]);
    
    // Get unique sectorIds from assetSubSectors and map them
    const uniqueSectorIds = new Set<number>();
    assetSubSectors.forEach(subSector => {
      uniqueSectorIds.add(subSector.sectorId);
    });
    
    uniqueSectorIds.forEach(sectorId => {
      const sectorName = sectorNames.get(sectorId) || `Sector ${sectorId}`;
      map.set(sectorId, sectorName);
    });
    
    return map;
  }, [assetSubSectors]);

  // Create entity name mapping
  const entityNameMap = useMemo(() => {
    const map = new Map<string, string>();
    entities.forEach(entity => {
      map.set(entity.id, entity.name);
    });
    return map;
  }, [entities]);

  // Apply filters to assets
  const filteredAssets = useMemo(() => {    
    const result = assetsData.filter(asset => {
      // Entity filter
      if (filters.entities.length > 0 && !filters.entities.includes(asset.entityId)) {
        return false;
      }
      
      // Asset type filter
      if (filters.assetClasses.length > 0) {
        const assetTypeName = asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) || 'Unknown' : 'Unknown';
        if (!filters.assetClasses.includes(assetTypeName)) {
          return false;
        }
      }
      
      // Sector filter
      if (filters.sectors.length > 0) {
        let sectorMatch = false;
        
        // Check if the selected filters include this asset's subsector
        if (asset.assetSubSectorId) {
          const subSectorName = assetSubSectorMap.get(asset.assetSubSectorId);
          if (subSectorName && filters.sectors.includes(subSectorName)) {
            sectorMatch = true;
          }
        }
        
        // Check if the selected filters include this asset's main sector
        if (!sectorMatch && asset.assetSubSectorId) {
          const subSector = assetSubSectors.find(s => s.id === asset.assetSubSectorId);
          if (subSector) {
            const sectorName = sectorMap.get(subSector.sectorId);
            if (sectorName && filters.sectors.includes(sectorName)) {
              sectorMatch = true;
            }
          }
        }
        
        if (!sectorMatch) {
          return false;
        }
      }

      // Portfolio filter
      if (filters.portfolios.length > 0 && !filters.portfolios.includes(asset.portfolioName)) {
        return false;
      }

      // Classification tree filter
      if (filters.classificationTree && filters.classificationTree.itemIds.length > 0) {
        const trees = generateClassificationTrees();
        const selectedTree = trees.find(tree => tree.id === filters.classificationTree!.treeId);
        
        if (selectedTree && selectedTree.items) {
          let treeMatch = false;
          
          // Check if asset is in any of the selected classification items (including children)
          for (const itemId of filters.classificationTree.itemIds) {
            const assetIds = getAssetIdsForTreeItem(selectedTree.items, itemId);
            
            if (assetIds.includes(asset.id)) {
              treeMatch = true;
              break;
            }
          }
          
          if (!treeMatch) {
            return false;
          }
        }
      }

      // Reportable filter
      if (filters.reportable === 'reportable' && !asset.reportable) {
        return false;
      }
      if (filters.reportable === 'non-reportable' && asset.reportable) {
        return false;
      }

      return true;
    });
    
    return result;
  }, [assetsData, filters, assetTypeMap, assetSubSectorMap, assetSubSectors, sectorMap]);

  // Calculate filtered total value
  const filteredTotalValue = useMemo(() => {
    return filteredAssets.reduce((sum, asset) => sum + asset.marketValueReporting, 0);
  }, [filteredAssets]);

  // Group filtered assets based on selected groupings
  const groupedAssets = useMemo(() => {
    return groupAssets(filteredAssets, selectedGroupings, entityNameMap);
  }, [filteredAssets, selectedGroupings, entityNameMap]);

  // Calculate allocations for the pie chart based on the first selected grouping
  const allocationData = useMemo(() => {
    if (selectedGroupings.length === 0) {
      return [];
    }

    const firstGrouping = selectedGroupings[0];
    const allocations = new Map<string, number>();
    
    filteredAssets.forEach(asset => {
      let key: string = 'Other'; // Initialize with default value
      
      switch (firstGrouping.type) {
        case 'entity':
          key = entityNameMap.get(asset.entityId) || 'Unknown Entity';
          break;
          
        case 'assetClass':
          key = asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) || 'Unknown Asset Type' : 'Unknown Asset Type';
          break;
          
        case 'sector':
          if (asset.assetSubSectorId) {
            const subSector = assetSubSectors.find(s => s.id === asset.assetSubSectorId);
            if (subSector) {
              key = sectorMap.get(subSector.sectorId) || 'Unknown Sector';
            } else {
              key = 'Unknown Sector';
            }
          } else {
            key = 'Unknown Sector';
          }
          break;
          
        case 'portfolio':
          key = asset.portfolioName || 'Unassigned';
          break;
          
        case 'classificationTree':
          if (firstGrouping.treeId && firstGrouping.level) {
            const trees = generateClassificationTrees();
            const tree = trees.find(t => t.id === firstGrouping.treeId);
            
            if (tree && tree.items) {
              const levelItems = tree.items.filter(item => item.level === firstGrouping.level);
              let found = false;
              
              for (const item of levelItems) {
                // Check if asset is directly in this item
                if (item.assetIds && item.assetIds.includes(asset.id)) {
                  key = item.name;
                  found = true;
                  break;
                }
                
                // Check if asset is in any descendant of this item using the helper function
                const assetIds = getAssetIdsForTreeItem(tree.items, item.id);
                if (assetIds.includes(asset.id)) {
                  key = item.name;
                  found = true;
                  break;
                }
              }
              
              if (!found) {
                key = 'Unclassified';
              }
            } else {
              key = 'Unclassified';
            }
          } else {
            key = 'Unclassified';
          }
          break;
          
        default:
          key = 'Other';
          break;
      }
      
      allocations.set(key, (allocations.get(key) || 0) + asset.marketValueReporting);
    });

    return Array.from(allocations.entries())
      .map(([name, value]) => ({
        name,
        value,
        percentage: (value / filteredTotalValue) * 100
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredAssets, filteredTotalValue, selectedGroupings, entityNameMap, assetTypeMap, assetSubSectors, sectorMap]);

  // Get the display label for the chart based on first selected grouping
  const chartLabel = useMemo(() => {
    if (selectedGroupings.length === 0) {
      return 'No Grouping Selected';
    }
    return selectedGroupings[0].label;
  }, [selectedGroupings]);

  // Colors for pie chart - Diverse color palette
  const COLORS = ['#8e24aa', '#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4', '#795548'];

  // Generate mock performance data for assets
  const generatePerformanceData = (asset: ValuationAsset) => {
    const currentValue = asset.marketValueReporting;
    const openValue = currentValue * (0.9 + Math.random() * 0.2); // Random open value ±10%
    const income = currentValue * (0.02 + Math.random() * 0.03); // Random income 2-5%
    const change = currentValue - openValue;
    const totalReturn = change + income;
    const returnPercent = (totalReturn / openValue) * 100;
    
    return {
      ...asset,
      openValue,
      closeValue: currentValue,
      change,
      income,
      totalReturn,
      returnPercent,
    };
  };

  // Create performance data for filtered assets
  // const performanceData = useMemo(() => {
  //   return filteredAssets.map(generatePerformanceData);
  // }, [filteredAssets]);

  const handlePeriodChange = (event: SelectChangeEvent<string>) => {
    setSelectedPeriod(event.target.value);
  };

  const shouldShowAllocations = useMemo(() => {
    return allocationData.length > 0;
  }, [allocationData]);

  const handleAccordionChange = (panel: string) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setExpandedAccordions(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(panel);
      } else {
        newSet.delete(panel);
      }
      return newSet;
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, assetId: string | number) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedAssetId(assetId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedAssetId(null);
  };

  const handleToggleReportable = () => {
    if (selectedAssetId !== null) {
      setAssetsData(prevAssets => 
        prevAssets.map(asset => 
          asset.id === selectedAssetId 
            ? { ...asset, reportable: !asset.reportable }
            : asset
        )
      );
    }
    handleMenuClose();
  };

  const handleBulkMenuClick = (event: React.MouseEvent<HTMLElement>, groupKey: string) => {
    event.stopPropagation(); // Prevent accordion toggle
    setSelectedGroupKey(groupKey);
    setBulkMenuAnchorEl(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchorEl(null);
    setSelectedGroupKey(null);
  };

  const getAllAssetsInGroup = (group: GroupedData): ValuationAsset[] => {
    let assets: ValuationAsset[] = [];
    
    if (group.assets) {
      assets = [...group.assets];
    }
    
    if (group.subGroups) {
      for (const subGroup of group.subGroups) {
        assets = [...assets, ...getAllAssetsInGroup(subGroup)];
      }
    }
    
    return assets;
  };

  const handleBulkToggleReportable = (makeReportable: boolean) => {
    if (selectedGroupKey !== null) {
      const findGroupByKey = (groups: GroupedData[], key: string): GroupedData | null => {
        for (const group of groups) {
          if (group.key === key) {
            return group;
          }
          if (group.subGroups) {
            const found = findGroupByKey(group.subGroups, key);
            if (found) return found;
          }
        }
        return null;
      };

      const selectedGroup = findGroupByKey(groupedAssets, selectedGroupKey);
      if (selectedGroup) {
        const assetsToUpdate = getAllAssetsInGroup(selectedGroup);
        const assetIds = assetsToUpdate.map(asset => asset.id);
        
        setAssetsData(prevAssets => 
          prevAssets.map(asset => 
            assetIds.includes(asset.id)
              ? { ...asset, reportable: makeReportable }
              : asset
          )
        );
      }
    }
    handleBulkMenuClose();
  };

  const renderGroupedTable = (groups: GroupedData[], level = 0, parentPath = ''): React.ReactElement[] => {
    return groups.map((group) => {
      const currentPath = parentPath ? `${parentPath}-${group.key}` : group.key;
      const uniqueKey = `${level}-${currentPath}`;
      
      return (
      <Accordion
        key={uniqueKey}
        expanded={expandedAccordions.has(uniqueKey)}
        onChange={handleAccordionChange(uniqueKey)}
        sx={{ 
          boxShadow: level === 0 ? 2 : 1, 
          mb: level === 0 ? 2 : 1,
          ml: level * 2,
          border: '1px solid',
          borderColor: '#e0e0e0',
          borderRadius: 1,
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: level === 0 ? '#f5f5f5' : '#fafafa',
            color: 'black',
            borderBottom: '1px solid #e0e0e0',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center'
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: 'black',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                {group.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {group.count} assets
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                  {formatCurrency(group.total)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {formatPercentage((group.total / filteredTotalValue) * 100)} of total
                </Typography>
              </Box>
              <Chip 
                label={group.count} 
                size="small" 
                variant="outlined"
                sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 'medium',
                  borderColor: '#8e24aa',
                  color: '#8e24aa'
                }}
              />
              <Box 
                component="span"
                onClick={(event) => handleBulkMenuClick(event, group.key)}
                sx={{ 
                  ml: 1,
                  p: 0.5,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 0 }}>
          {group.subGroups && group.subGroups.length > 0 ? (
            // Render sub-groups
            <Box sx={{ pl: 2 }}>
              {renderGroupedTable(group.subGroups, level + 1, currentPath)}
            </Box>
          ) : (
            // Render assets table
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Asset</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Code/ Exchange</TableCell>
                    <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Units</TableCell>
                    <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Currency</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Pending Settlement</TableCell>
                    <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Accrued Income</TableCell>
                    <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Value</TableCell>
                    <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>%</TableCell>
                    <TableCell align="center" sx={{ color: 'black', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.assets.map((asset) => (
                    <TableRow key={asset.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {asset.assetName}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) || 'Unknown' : 'Unknown'}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        {asset.investmentCode ? (
                          <Box>
                            <Typography variant="body2" component="div" fontWeight="medium">
                              {asset.investmentCode}
                            </Typography>
                            {asset.exchange && (
                              <Chip 
                                label={asset.exchange} 
                                size="small" 
                                variant="outlined" 
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      
                      <TableCell align="right">
                        {asset.isUnitised && asset.unitsHeld 
                          ? new Intl.NumberFormat().format(asset.unitsHeld) 
                          : '-'
                        }
                      </TableCell>
                      
                      <TableCell align="right">
                        {asset.isUnitised && asset.unitPrice 
                          ? formatCurrency(asset.unitPrice, asset.priceBaseCurrency) 
                          : '-'
                        }
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {asset.priceBaseCurrency}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        {asset.isUnitised && asset.priceDate 
                          ? formatDate(asset.priceDate) 
                          : '-'
                        }
                      </TableCell>
                      
                      <TableCell align="right">
                        {asset.pendingSettlement 
                          ? formatCurrency(asset.pendingSettlement, asset.priceBaseCurrency)
                          : '-'
                        }
                      </TableCell>
                      
                      <TableCell align="right">
                        {asset.accruedIncome 
                          ? formatCurrency(asset.accruedIncome, asset.priceBaseCurrency)
                          : '-'
                        }
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                          {formatCurrency(asset.marketValueReporting)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatCurrency(asset.marketValueDomicile, asset.priceBaseCurrency)} {asset.priceBaseCurrency}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatPercentage(asset.percentAllocation)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(event) => handleMenuOpen(event, asset.id)}
                          aria-label="asset actions"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
      );
    });
  };

  const renderPerformanceTable = (groups: GroupedData[], level = 0, parentPath = ''): React.ReactElement[] => {
    return groups.map((group) => {
      const currentPath = parentPath ? `${parentPath}-${group.key}` : group.key;
      const uniqueKey = `${level}-${currentPath}`;
      
      // Calculate performance metrics for the group
      const groupPerformanceAssets = group.assets.map(generatePerformanceData);
      const groupTotalOpen = groupPerformanceAssets.reduce((sum, asset) => sum + asset.openValue, 0);
      const groupTotalClose = groupPerformanceAssets.reduce((sum, asset) => sum + asset.closeValue, 0);
      const groupTotalIncome = groupPerformanceAssets.reduce((sum, asset) => sum + asset.income, 0);
      const groupTotalChange = groupTotalClose - groupTotalOpen;
      const groupTotalReturn = groupTotalChange + groupTotalIncome;
      const groupReturnPercent = groupTotalOpen > 0 ? (groupTotalReturn / groupTotalOpen) * 100 : 0;
      
      return (
        <Accordion
          key={uniqueKey}
          expanded={expandedAccordions.has(uniqueKey)}
          onChange={handleAccordionChange(uniqueKey)}
          sx={{ 
            boxShadow: level === 0 ? 2 : 1, 
            mb: level === 0 ? 2 : 1,
            ml: level * 2,
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: 1,
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: level === 0 ? '#f5f5f5' : '#fafafa',
              color: 'black',
              borderBottom: '1px solid #e0e0e0',
              '& .MuiAccordionSummary-content': {
                alignItems: 'center'
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: 'black',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  {group.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {group.count} assets
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ color: groupReturnPercent >= 0 ? '#2e7d32' : '#d32f2f', fontWeight: 'bold' }}>
                    {groupReturnPercent >= 0 ? '+' : ''}{groupReturnPercent.toFixed(2)}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Return
                  </Typography>
                </Box>
                <Chip 
                  label={group.count} 
                  size="small" 
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem', 
                    fontWeight: 'medium',
                    borderColor: '#8e24aa',
                    color: '#8e24aa'
                  }}
                />
                <Box 
                  component="span"
                  onClick={(event) => handleBulkMenuClick(event, group.key)}
                  sx={{ 
                    ml: 1,
                    p: 0.5,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </Box>
              </Box>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 0 }}>
            {group.subGroups && group.subGroups.length > 0 ? (
              // Render sub-groups
              <Box sx={{ pl: 2 }}>
                {renderPerformanceTable(group.subGroups, level + 1, currentPath)}
              </Box>
            ) : (
              // Render performance table
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Asset</TableCell>
                      <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Open Value</TableCell>
                      <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Close Value</TableCell>
                      <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Change</TableCell>
                      <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Income</TableCell>
                      <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Return</TableCell>
                      <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Return %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupPerformanceAssets.map((asset) => (
                      <TableRow key={asset.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {asset.assetName}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) || 'Unknown' : 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(asset.openValue)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(asset.closeValue)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            fontWeight="medium" 
                            sx={{ color: asset.change >= 0 ? '#2e7d32' : '#d32f2f' }}
                          >
                            {asset.change >= 0 ? '+' : ''}{formatCurrency(asset.change)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium" sx={{ color: '#2e7d32' }}>
                            +{formatCurrency(asset.income)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            fontWeight="bold" 
                            sx={{ color: asset.totalReturn >= 0 ? '#2e7d32' : '#d32f2f' }}
                          >
                            {asset.totalReturn >= 0 ? '+' : ''}{formatCurrency(asset.totalReturn)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            fontWeight="bold" 
                            sx={{ color: asset.returnPercent >= 0 ? '#2e7d32' : '#d32f2f' }}
                          >
                            {asset.returnPercent >= 0 ? '+' : ''}{asset.returnPercent.toFixed(2)}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  return (
    <Paper sx={{ p: 3, width: '100%', boxShadow: 3, backgroundColor: '#fafafa' }}>
      <Box sx={{ textAlign: 'center', mb: 4, py: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'black', fontWeight: 'bold' }}>
          Valuation for {entityGroup.name}
        </Typography>
      </Box>
      
      <Box sx={{ mb: 4, p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid', borderColor: '#e0e0e0', boxShadow: 1 }}>
        <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold', mb: 1 }}>
          Total Value: <span style={{ color: '#2e7d32' }}>{formatCurrency(totalValue)}</span>
        </Typography>
        {filteredTotalValue !== totalValue && (
          <Typography variant="body2" sx={{ color: 'black', fontWeight: 'medium' }}>
            Filtered Value: <strong style={{ color: '#2e7d32' }}>{formatCurrency(filteredTotalValue)}</strong> 
            ({formatPercentage((filteredTotalValue / totalValue) * 100)} of total)
          </Typography>
        )}
      </Box>

      {/* Filter / Group Section */}
      <Accordion defaultExpanded sx={{ mb: 4, border: '1px solid', borderColor: '#e0e0e0', borderRadius: 2, boxShadow: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            bgcolor: '#f5f5f5',
            color: 'black',
            borderRadius: '8px 8px 0 0',
            '&.Mui-expanded': {
              minHeight: 48,
              borderRadius: '8px 8px 0 0',
            },
            '& .MuiAccordionSummary-content': {
              '&.Mui-expanded': {
                margin: '12px 0',
              },
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: 'black',
            },
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'black',
              fontWeight: 'bold',
            }}
          >
            Filter / Group
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, bgcolor: 'white' }}>
          <FilterSelector
            assets={assets}
            entities={entities}
            filters={filters}
            onFiltersChange={setFilters}
          />

          <GroupingSelector
            selectedGroupings={selectedGroupings}
            onGroupingsChange={setSelectedGroupings}
          />
        </AccordionDetails>
      </Accordion>

      {/* Results Section */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange} 
          aria-label="valuation tabs"
          sx={{
            borderBottom: 2,
            borderColor: '#8e24aa',
            '& .MuiTab-root': {
              fontWeight: 'bold',
              fontSize: '1rem',
              '&.Mui-selected': {
                color: '#8e24aa',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#8e24aa',
              height: 3,
            },
          }}
        >
          <Tab label="Valuation Table" />
          <Tab label="Asset Allocation" />
          <Tab label="Performance" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box>
          {renderGroupedTable(groupedAssets)}
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          {/* Allocation Chart and Table */}
          {shouldShowAllocations && allocationData.length > 0 ? (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: 'black', fontWeight: 'bold' }}>
                Asset Allocation
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                By {chartLabel} (from Group By selection)
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                {/* Allocation Table */}
                <Box sx={{ flex: 1 }}>
                  <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: '#e0e0e0', borderRadius: 2 }}>
                    <Table>
                      <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>{chartLabel}</TableCell>
                          <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>Value</TableCell>
                          <TableCell align="right" sx={{ color: 'black', fontWeight: 'bold' }}>%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allocationData.map((item, index) => (
                          <TableRow 
                            key={item.name}
                            onMouseEnter={() => setHoveredSlice(item.name)}
                            onMouseLeave={() => setHoveredSlice(null)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { 
                                bgcolor: 'action.hover' 
                              }
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box 
                                  sx={{ 
                                    width: 12, 
                                    height: 12, 
                                    bgcolor: COLORS[index % COLORS.length],
                                    borderRadius: 1
                                  }} 
                                />
                                {item.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.value)}
                            </TableCell>
                            <TableCell align="right">
                              {item.percentage.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Pie Chart */}
                <Box sx={{ flex: '0 0 40%', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={allocationData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {allocationData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[index % COLORS.length]}
                            stroke={hoveredSlice === entry.name ? '#000' : 'none'}
                            strokeWidth={hoveredSlice === entry.name ? 2 : 0}
                            opacity={hoveredSlice === null || hoveredSlice === entry.name ? 1 : 0.6}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(_value, name, props) => [`${name} (${props.payload.percentage.toFixed(1)}%)`]} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </>
          ) : (
            <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
              No allocation data available with current filters
            </Typography>
          )}
        </Box>
      )}

      {selectedTab === 2 && (
        <Box>
          {/* Period Selection */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Performance Period</InputLabel>
              <Select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                label="Performance Period"
              >
                <MenuItem value="last-30-days">Last 30 Days</MenuItem>
                <MenuItem value="last-3-months">Last 3 Months</MenuItem>
                <MenuItem value="last-6-months">Last 6 Months</MenuItem>
                <MenuItem value="last-year">Last Year</MenuItem>
                <MenuItem value="last-3-years">Last 3 Years</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>

            {selectedPeriod === 'custom' && (
              <>
                <TextField
                  label="Start Date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
              </>
            )}
          </Box>

          {/* Performance Section */}
          <Typography variant="h6" gutterBottom sx={{ color: 'black', fontWeight: 'bold' }}>
            Performance Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Performance metrics for the selected period and assets
          </Typography>
          
          {renderPerformanceTable(groupedAssets)}
        </Box>
      )}

      {/* Asset Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedAssetId && (() => {
          const selectedAsset = assetsData.find(asset => asset.id === selectedAssetId);
          return selectedAsset ? (
            <MenuItem onClick={handleToggleReportable}>
              <ListItemIcon>
                {selectedAsset.reportable ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </ListItemIcon>
              <ListItemText>
                {selectedAsset.reportable ? 'Mark as Non-Reportable' : 'Mark as Reportable'}
              </ListItemText>
            </MenuItem>
          ) : null;
        })()}
      </Menu>

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkMenuAnchorEl}
        open={Boolean(bulkMenuAnchorEl)}
        onClose={handleBulkMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {filters.reportable === 'reportable' && (
          <MenuItem onClick={() => handleBulkToggleReportable(false)}>
            <ListItemIcon>
              <VisibilityOffIcon />
            </ListItemIcon>
            <ListItemText>
              Mark all as Non-Reportable
            </ListItemText>
          </MenuItem>
        )}
        {filters.reportable === 'non-reportable' && (
          <MenuItem onClick={() => handleBulkToggleReportable(true)}>
            <ListItemIcon>
              <VisibilityIcon />
            </ListItemIcon>
            <ListItemText>
              Mark all as Reportable
            </ListItemText>
          </MenuItem>
        )}
        {filters.reportable === 'all' && (
          <>
            <MenuItem onClick={() => handleBulkToggleReportable(true)}>
              <ListItemIcon>
                <VisibilityIcon />
              </ListItemIcon>
              <ListItemText>
                Mark all as Reportable
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleBulkToggleReportable(false)}>
              <ListItemIcon>
                <VisibilityOffIcon />
              </ListItemIcon>
              <ListItemText>
                Mark all as Non-Reportable
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Paper>
  );
};

export default ValuationTableV1;
